import {
  AfterContentInit,
  Component,
  ContentChildren,
  ElementRef,
  Input,
  OnInit,
  QueryList,
} from '@angular/core';
import { AbstractControl, ControlContainer, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { ValidationErrorDirective } from '@app/shared/directives/validation-error.directive';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'portal-form-validation-errors',
  templateUrl: './form-validation-errors.component.html',
  host: {
    class: 'position-relative',
  },
  standalone: false,
})
export class FormValidationErrorsComponent implements AfterContentInit, OnInit {
  @ContentChildren(ValidationErrorDirective, { descendants: false })
  validationErrors: QueryList<ValidationErrorDirective>;

  @Input()
  controlName: string;

  @Input()
  errorStyle: 'default' | 'absolute' | 'large' = 'default';

  /**
   * A list of keys for which no error message is displayed.
   */
  @Input()
  ignoreErrors?: string[];

  /**
   * Can be used to display a custom message if any error occurred
   */
  @Input()
  message?: string;

  @Input()
  showIfFormGroupIsInvalid: boolean;

  @Input()
  skipValidationIfPristine: boolean;

  control: AbstractControl;
  errors: ValidationErrors = null;

  /**
   * If some validation error directive has been passed that would display an error using the
   * current errors.
   */
  hasCustomValidationMessage: boolean = false;

  get style() {
    return `alert-${this.errorStyle}`;
  }

  get skipValidation(): boolean {
    let skip = true;

    if (this.control.pristine && this.skipValidationIfPristine) {
      skip = true;
    } else if (this.control.invalid) {
      skip = false;
    } else if (this.showIfFormGroupIsInvalid && this.form.invalid) {
      skip = false;
    }

    return skip;
  }

  constructor(
    private elementRef: ElementRef,
    private form: ControlContainer
  ) {}

  ngAfterContentInit() {
    this.updateErrors();
  }

  ngOnInit() {
    this.form.control.statusChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateErrors();
    });

    if (this.controlName === undefined) {
      this.control = this.form.control;
    } else {
      this.control = (this.form.control as UntypedFormGroup).get(this.controlName) as AbstractControl;
    }

    this.skipValidationIfPristine = this.skipValidationIfPristine !== undefined;

    $(this.elementRef.nativeElement).closest('.form-group').css({
      position: 'relative',
    });
  }

  private updateErrors() {
    const errors: ValidationErrors = {};

    if (this.validationErrors) {
      if (this.form.control.errors) {
        this.validationErrors.forEach((directive) => {
          if (this.form.control.errors[directive.portalValidationError]) {
            errors[directive.portalValidationError] =
              this.form.control.errors[directive.portalValidationError];
          }
        });
      }
    }

    if (this.controlName != null) {
      Object.entries(this.form.control.get(this.controlName).errors ?? {}).forEach(([key, error]) => {
        errors[key] = error;
      });
    }

    if (this.showIfFormGroupIsInvalid && this.form.errors) {
      Object.assign(errors, this.form.errors);
    }

    this.errors = Object.keys(errors).length > 0 ? errors : null;
    this.hasCustomValidationMessage = Object.keys(errors)
      .map(
        (key) => this.validationErrors?.some((directive) => directive.portalValidationError === key) ?? false
      )
      .reduce((acc, hasErrorTemplate) => acc || hasErrorTemplate, false);
  }
}
